import { useEffect, useState, type FormEvent } from "react";
import { ApiRequestError, createApiClient, createAuthClient } from "@pustak-lms/api-client";
import type { SessionIdentity } from "@pustak-lms/contracts";
import { BrandLogo } from "./brand";
const api = createApiClient({ baseUrl: "" });
const auth = createAuthClient();
interface InvitationDetails { email: string; memberType: string; existingAccount: boolean; expiresAt: string; batchId?: string | null; batchName?: string | null }
export function ActivationPage({ surface }: { surface: "manage" | "learn" }) {
  const [token] = useState(() => new URLSearchParams(window.location.search).get("token") ?? new URLSearchParams(window.location.hash.slice(1)).get("token") ?? "");
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [session, setSession] = useState<SessionIdentity | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [complete, setComplete] = useState(false);
  useEffect(() => {
    let active = true;
    // Keep refresh recovery without sending the bearer token on subsequent page requests.
    if (token) window.history.replaceState(null, "", `${window.location.pathname}#token=${encodeURIComponent(token)}`);
    Promise.all([api.post<InvitationDetails>("/api/invitations/inspect", { token, surface }), auth.getSession()])
      .then(([details, identity]) => { if (active) { setInvitation(details); setSession(identity); } })
      .catch((e: unknown) => { if (active) setError(e instanceof Error ? e.message : "Invitation unavailable"); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [token, surface]);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); if (!invitation) return;
    const form = new FormData(event.currentTarget); setBusy(true); setError("");
    try {
      if (invitation.existingAccount && !session) {
        await auth.signIn(invitation.email, String(form.get("password")));
        const identity = await auth.getSession();
        if (!identity) throw new Error("Unable to restore your session. Please sign in again.");
        setSession(identity);
        return;
      }
      const body: Record<string, string> = { token, surface };
      if (!invitation.existingAccount) {
        body.name = String(form.get("name")); body.password = String(form.get("password")); body.confirmPassword = String(form.get("confirmPassword"));
        if (body.password !== body.confirmPassword) { setError("Passwords do not match"); return; }
      }
      await api.post("/api/invitations/activate", body);
      window.history.replaceState(null, "", window.location.pathname);
      setComplete(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Activation failed. Please try again.");
      // A retry must detect an identity created before a membership persistence failure.
      try { setInvitation(await api.post<InvitationDetails>("/api/invitations/inspect", { token, surface })); } catch { /* Keep the original safe error. */ }
      if (e instanceof ApiRequestError && e.code === "LOGIN_REQUIRED") setSession(null);
      if (e instanceof ApiRequestError && e.code === "WRONG_ACCOUNT") {
        try { setSession(await auth.getSession()); } catch { setSession(null); }
      }
    } finally { setBusy(false); }
  }
  async function signOut() {
    setBusy(true);
    try { await auth.signOut(); setSession(null); setError(""); } catch { setError("Unable to sign out. Please try again."); }
    finally { setBusy(false); }
  }
  const wrongUser = session && invitation && session.user.email.toLowerCase() !== invitation.email.toLowerCase();
  return <main className="auth-screen"><section className="auth-card">
    <BrandLogo className="auth-brand" /><h1>{complete ? "Invitation accepted" : "Activate your account"}</h1>
    {loading ? <p role="status">Checking your invitation…</p> : null}
    {error ? <p role="alert" className="auth-error">{error}</p> : null}
    {complete ? <><p>{invitation?.batchName ? `Your enrollment in ${invitation.batchName} is ready.` : "Your organization membership is ready."}</p><a href={`/${surface}${session ? "" : "/login"}`}>{session ? "Open your workspace" : "Continue to sign in"}</a></> : invitation ? <>
      {wrongUser ? <><p>Sign out and use the invited email address.</p><button type="button" disabled={busy} onClick={() => void signOut()}>Sign out</button></> : null}
      {invitation.batchName ? <p>Join batch: <strong>{invitation.batchName}</strong></p> : null}
      <form onSubmit={(e) => void submit(e)}>
        <label htmlFor="invited-email">Invited email</label><input id="invited-email" value={invitation.email} type="email" readOnly />
        {!invitation.existingAccount ? <><label htmlFor="activation-name">Name</label><input id="activation-name" name="name" autoComplete="name" required maxLength={200} disabled={busy} /></> : <p>Use your existing Pustak LMS account to accept this invitation.</p>}
        {!invitation.existingAccount || !session ? <><label htmlFor="activation-password">Password</label><input id="activation-password" name="password" type="password" autoComplete={invitation.existingAccount ? "current-password" : "new-password"} required minLength={invitation.existingAccount ? undefined : 12} maxLength={128} disabled={busy} /></> : null}
        {!invitation.existingAccount ? <><label htmlFor="activation-confirm">Confirm password</label><input id="activation-confirm" name="confirmPassword" type="password" autoComplete="new-password" required maxLength={128} disabled={busy} /></> : null}
        <button className="button button-primary" type="submit" disabled={busy || Boolean(wrongUser)}>{busy ? "Please wait…" : invitation.existingAccount ? session ? "Accept invitation" : "Sign in" : "Activate account"}</button>
      </form>
      {invitation.existingAccount && !session ? <a href={`/${surface}/forgot-password`} target="_blank" rel="noopener noreferrer">Forgot password?</a> : null}
    </> : null}
  </section></main>;
}
